export interface ProgramDefinition {
  /**
   * Program definition ID.
   * @format GUID
   * @immutable
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the program definition is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the program definition.
   *
   * Ignored when creating a program definition.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the program definition was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the program definition was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Program definition name.
   * @maxLength 64
   */
  displayName?: string | null;
  /**
   * Program definition description.
   * @internal
   * @maxLength 450
   */
  description?: string | null;
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @immutable
   * @readonly
   * @minLength 1
   * @maxLength 20
   */
  namespace?: string | null;
  /**
   * Custom field data for the program definition object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * ID for the program definition defined by you. You can use `externalId` to filter queries.
   * @format GUID
   * @immutable
   * @readonly
   */
  externalId?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface ProgramDefinitionTranslated {
  /** The translated program definition. */
  programDefinition?: ProgramDefinition;
  /**
   * The language code of the translation.
   * @maxLength 20
   * @format LANGUAGE_TAG
   */
  language?: string;
}

export interface ProgramDefinitionTranslationDeleted {
  /**
   * The ID of the program definition.
   * @format GUID
   */
  programDefinitionId?: string;
  /**
   * The language code of the translation.
   * @maxLength 20
   * @format LANGUAGE_TAG
   */
  language?: string;
}

export interface CreateProgramDefinitionRequest {
  /** Program definition to create. */
  programDefinition: ProgramDefinition;
}

export interface CreateProgramDefinitionResponse {
  /** Created program definition. */
  programDefinition?: ProgramDefinition;
}

export interface UpdateProgramDefinitionRequest {
  /** Program definition to update. */
  programDefinition: ProgramDefinition;
  /**
   * Explicit list of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateProgramDefinitionResponse {
  /** Updated program definition. */
  programDefinition?: ProgramDefinition;
}

export interface DeleteProgramDefinitionRequest {
  /**
   * ID of the program definition to delete.
   * @format GUID
   */
  programDefinitionId: string;
  /** Determines when the changes to this program definition will be applied to associated programs and pools. */
  cascadeType?: CascadeWithLiterals;
}

export enum Cascade {
  /** Unknown cascade. */
  UNKNOWN_CASCADE = 'UNKNOWN_CASCADE',
  /** Changes are applied to existing programs and pools when they are next renewed. */
  NEXT_RENEWAL = 'NEXT_RENEWAL',
  /** Changes are applied to associated programs and pools immediately. */
  IMMEDIATELY = 'IMMEDIATELY',
  /** Changes are not applied to existing associated programs and pools. They are only applied to future programs and pools. */
  FUTURE_PROVISIONS = 'FUTURE_PROVISIONS',
}

/** @enumType */
export type CascadeWithLiterals =
  | Cascade
  | 'UNKNOWN_CASCADE'
  | 'NEXT_RENEWAL'
  | 'IMMEDIATELY'
  | 'FUTURE_PROVISIONS';

export interface DeleteProgramDefinitionResponse {}

export interface GetProgramDefinitionRequest {
  /**
   * ID of the program definition to retrieve.
   * @format GUID
   */
  programDefinitionId: string;
}

export interface GetProgramDefinitionResponse {
  /** Retrieved program definition. */
  programDefinition?: ProgramDefinition;
}

export interface GetProgramDefinitionByExternalIdAndNamespaceRequest {
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @minLength 1
   * @maxLength 20
   */
  namespace: string;
  /**
   * Program definition external ID.
   * @format GUID
   */
  externalId: string;
}

export interface GetProgramDefinitionByExternalIdAndNamespaceResponse {
  /** Retrieved program definition. */
  programDefinition?: ProgramDefinition;
}

export interface QueryProgramDefinitionsRequest {
  /** Filter, sort, and paging to apply to the query. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   * See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
   * for more information.
   */
  filter?: Record<string, any> | null;
  /**
   * List of sort objects.
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /**
   * Sort order. Use `ASC` for ascending order or `DESC` for descending order.
   *
   * Default: `ASC`
   */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
}

export enum SortOrder {
  /** Ascending sort order. */
  ASC = 'ASC',
  /** Descending sort order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryProgramDefinitionsResponse {
  /** List of retrieved program definitions. */
  programDefinitions?: ProgramDefinition[];
  /** Metadata for the paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpsertProgramDefinitionRequest {
  /** Program definition to be created or updated. */
  programDefinition?: ProgramDefinition;
  /**
   * Explicit list of fields to update
   * @internal
   */
  fieldMask?: string[];
}

export interface UpsertProgramDefinitionResponse {
  /** Created or updated program definition. */
  programDefinition?: ProgramDefinition;
}

export interface GetOrCreateProgramDefinitionRequest {
  /**
   * Application sub-module that represents the source of the program definition. Specified by the external developer. This namespace is used to handle and retrieve program definition and associated programs.
   * @minLength 1
   * @maxLength 20
   */
  namespace?: string;
  /**
   * External program definition ID.
   * @format GUID
   */
  externalId?: string;
}

export interface GetOrCreateProgramDefinitionResponse {
  /** The created or updated program definition. */
  programDefinition?: ProgramDefinition;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
